import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { fillPager, listPer, rememberPer, searchBox } from "../../ui/list";
import { openModal } from "../../ui/modal";
import { chip, ib } from "../../ui/record";
import { uploadFile } from "../../ui/upload";
import { field, refuse, select } from "../clients/forms";
import { openVisitRecord } from "../patients/validate";
import type { PerPage } from "../../../server/rules/list-page";

// Worked visits (brief 1E-33, mockup v87 wvCard): one list of the visits that were checked out, in
// four places with the same rows, columns and pencil: the patient's Billing and payroll, the
// caregiver's Earnings, Home Care › Billing (starts on Not billed) and Home Care › Payroll (starts
// on Not sent). Period, Group by with a total line, filters on every status, search and pages;
// actions on ticked rows; the pencil changes a visit with a reason (the office only).

type Place = "patient" | "caregiver" | "billing" | "payroll";
interface Status {
  text: string;
  level: string;
}
interface Row {
  id: string;
  date: string;
  caregiver: string;
  title: string;
  patient: string;
  client: string;
  service: string;
  worked: string;
  times: string;
  billHours: string;
  bill: string | null;
  pay: string | null;
  payValue: string | null;
  validation: Status;
  billing: Status;
  payroll: Status;
  documents: number;
  group: string | null;
  groupKey: string | null;
  edit: boolean;
}
interface Totals {
  visits: number;
  worked: string;
  billHours: string;
  bill: string | null;
  pay: string | null;
}
interface Answer {
  title: string;
  period: { key: string; label: string; from: string; to: string; error: string | null };
  periods: { value: string; label: string }[];
  group: string;
  groups: { value: string; label: string }[];
  filters: { validation: string | null; billing: string | null; payroll: string | null };
  choices: { validation: string[]; billing: string[]; payroll: string[] };
  columns: { caregiver: boolean; patient: boolean; bill: boolean; pay: boolean };
  rows: Row[];
  groupTotals: (Totals & { key: string; label: string })[];
  totals: Totals;
  can: {
    validate: boolean;
    invoice: boolean;
    payroll: boolean;
    edit: boolean;
    billHours: boolean;
    pay: boolean;
  };
  correctionReasons: string[];
  fileKinds: { id: string; name: string }[];
  total: number;
  page: number;
  per: PerPage;
  fromRow: number;
  toRow: number;
  pages: number;
}

/** The card, for one place; `id` is the patient's or the caregiver's. Reloads after each change. */
export function workedVisitsCard(
  ctx: ScreenContext,
  place: Place,
  id: string | null = null,
): HTMLElement & {
  reload: () => Promise<void>;
  fix: (visitId: string, day: string) => Promise<void>;
} {
  const listId = `worked-${place}`;
  const state = {
    period: "this",
    from: "",
    to: "",
    group: "none",
    // null: the place's own starting filter; "all" clears it.
    validation: null as string | null,
    billing: null as string | null,
    payroll: null as string | null,
    q: "",
    page: 1,
    per: listPer(listId, ctx.me.user.id),
  };
  let ticked = new Set<string>();
  let last: Answer | null = null;
  const headTitle = el("h2", {}, icon("checklist"), " Worked visits");
  const exportBtn = el("button", { type: "button", class: "btn sm" }, icon("download"), " Export");
  const bar = el("div", { class: "wvbar", "data-unsaved": "off" });
  const dates = el("div", { class: "wvbar", "data-unsaved": "off" });
  const acts = el("div", { class: "wvact", hidden: true });
  const tableSlot = el("div", { "aria-live": "polite" });
  const pager = el("div", { class: "pager" });
  const search = searchBox("Search caregiver, patient, client or service", (text) => {
    state.q = text;
    state.page = 1;
    void load();
  });
  const host = el(
    "section",
    { class: "card wvcard", "data-worked": place },
    el(
      "div",
      { class: "cardhead" },
      el(
        "div",
        {},
        headTitle,
        el("span", { class: "small muted" }, "A visit shows here once it's checked out."),
      ),
      el("div", { class: "rowacts" }, exportBtn),
    ),
    bar,
    dates,
    search,
    acts,
    tableSlot,
    pager,
  ) as HTMLElement & {
    reload: () => Promise<void>;
    fix: (visitId: string, day: string) => Promise<void>;
  };
  // Its folding name stays the same when the title names the caregiver.
  host.setAttribute("aria-label", "Worked visits");

  const params = (page = state.page, per: number = state.per) => {
    const p = new URLSearchParams({ place, period: state.period, group: state.group });
    if (id) p.set("id", id);
    if (state.period === "custom") {
      p.set("from", state.from);
      p.set("to", state.to);
    }
    for (const k of ["validation", "billing", "payroll"] as const)
      if (state[k] !== null) p.set(k, state[k] as string);
    if (state.q) p.set("q", state.q);
    p.set("page", String(page));
    p.set("per", String(per));
    return p;
  };

  let seq = 0;
  async function load() {
    const mine = ++seq;
    const res = await api<Answer>("GET", `/api/worked-visits?${params()}`);
    if (mine !== seq) return;
    // A place this person doesn't reach leaves no card behind.
    if (!res.ok && res.status === 404) return host.remove();
    if (!res.ok) {
      tableSlot.replaceChildren(
        el("p", { class: "note", role: "alert" }, "Worked visits couldn't be loaded."),
      );
      return;
    }
    last = res.body;
    draw(res.body);
  }
  host.reload = load;
  // Brief 1E-35: Fix the times on a held visit opens its pencil, the list showing that day's Held.
  host.fix = async (visitId: string, day: string) => {
    Object.assign(state, { period: "custom", from: day, to: day, billing: "Held", page: 1 });
    await load();
    const r = last?.rows.find((x) => x.id === visitId);
    if (last && r) {
      host.scrollIntoView({ block: "start" });
      await pencil(last, r);
    }
  };

  function pick(
    key: "period" | "group" | "validation" | "billing" | "payroll",
    label: string,
    options: readonly (readonly [string, string])[],
    value: string,
  ) {
    const s = select(label, options, value);
    s.input.dataset.wvf = key;
    s.input.addEventListener("change", () => {
      state[key] = s.input.value;
      state.page = 1;
      ticked = new Set();
      void load();
    });
    return s.el;
  }

  function draw(a: Answer) {
    headTitle.replaceChildren(icon("checklist"), ` ${a.title}`);
    const all = (list: readonly string[]) => [
      ["all", "All"] as const,
      ...list.map((v) => [v, v] as const),
    ];
    bar.replaceChildren(
      pick(
        "period",
        "Period",
        a.periods.map((p) => [p.value, p.label] as const),
        a.period.key,
      ),
      pick(
        "group",
        "Group by",
        a.groups.map((g) => [g.value, g.label] as const),
        a.group,
      ),
      pick("validation", "Validation", all(a.choices.validation), a.filters.validation ?? "all"),
      pick("billing", "Billing", all(a.choices.billing), a.filters.billing ?? "all"),
      pick("payroll", "Payroll", all(a.choices.payroll), a.filters.payroll ?? "all"),
    );
    if (a.period.key === "custom") {
      const from = field("From", state.from || a.period.from, { placeholder: "MM/DD/YYYY" });
      const to = field("To", state.to || a.period.to, { placeholder: "MM/DD/YYYY" });
      const go = () => {
        state.from = from.input.value;
        state.to = to.input.value;
        state.page = 1;
        void load();
      };
      from.input.addEventListener("change", go);
      to.input.addEventListener("change", go);
      dates.replaceChildren(
        from.el,
        to.el,
        ...(a.period.error ? [el("p", { class: "small", role: "alert" }, a.period.error)] : []),
      );
      dates.hidden = false;
    } else {
      dates.replaceChildren();
      dates.hidden = true;
    }
    drawActions(a);
    tableSlot.replaceChildren(
      a.rows.length ? tableOf(a) : el("p", { class: "muted" }, "No worked visits match."),
    );
    fillPager(pager, a, a.per, {
      per: async (n) => {
        state.per = n;
        state.page = 1;
        rememberPer(listId, ctx.me.user.id, n);
        await load();
      },
      page: async (n) => {
        state.page = n;
        await load();
      },
    });
  }

  function drawActions(a: Answer) {
    const n = ticked.size;
    acts.hidden = n === 0;
    if (!n) return acts.replaceChildren();
    const btn = (label: string, iconName: string, action: string, pri = false) => {
      const b = el(
        "button",
        { type: "button", class: `btn sm${pri ? " pri" : ""}` },
        icon(iconName),
        ` ${label}`,
      );
      b.addEventListener("click", () => void act(action, b));
      return b;
    };
    const clear = el("button", { type: "button", class: "btn sm" }, "Clear");
    clear.addEventListener("click", () => {
      ticked = new Set();
      if (last) draw(last);
    });
    acts.replaceChildren(
      el("b", {}, `${n} selected`),
      ...(a.can.validate ? [btn("Validate", "check", "validate")] : []),
      ...(a.can.invoice ? [btn("Create invoice", "file", "invoice")] : []),
      ...(a.can.payroll
        ? [btn("Approve and send to payroll", "credit-card", "payroll", true)]
        : []),
      clear,
    );
  }

  async function act(action: string, b: HTMLButtonElement, reason?: string) {
    b.disabled = true;
    const r = await api<{
      message: string;
      sub: string | null;
      needsReason?: number;
      done?: number;
    }>("POST", "/api/worked-visits/actions", {
      action,
      ids: [...ticked],
      ...(reason ? { reason } : {}),
    });
    b.disabled = false;
    if (!r.ok) return toast("That didn't go through", r.body.error);
    // Visits with exceptions are validated with a reason, asked once for all of them.
    if (action === "validate" && r.body.needsReason && !reason) {
      const why = field("Reason", "", {
        maxlength: "300",
        placeholder: "e.g. Confirmed with the family by phone",
      });
      const go = el("button", { type: "button", class: "btn pri" }, icon("check"), " Validate");
      const n = r.body.needsReason;
      const m = openModal({
        title: "Reason for validating",
        sub: `${n} visit${n === 1 ? " has" : "s have"} exceptions.`,
        body: [el("div", { class: "form", style: "max-width:none" }, why.el)],
        actions: [go],
      });
      why.input.focus();
      go.addEventListener("click", async () => {
        if (!why.input.value.trim()) return refuse(m.note, "Say briefly why they're validated.");
        m.close();
        await act("validate", b, why.input.value);
      });
      return;
    }
    toast(r.body.message, r.body.sub ?? "Each change is in the visit's history");
    ticked = new Set();
    await load();
  }

  function tableOf(a: Answer): HTMLElement {
    type Col = [string, (r: Row) => Node | string, boolean?];
    const cols: Col[] = [
      ["Date", (r) => r.date],
      ...(a.columns.caregiver
        ? [
            [
              "Caregiver",
              (r: Row) =>
                el(
                  "span",
                  {},
                  r.caregiver,
                  el("br"),
                  el("span", { class: "small muted" }, r.title),
                ),
            ] as Col,
          ]
        : []),
      ...(a.columns.patient
        ? [
            [
              "Patient",
              (r: Row) =>
                el("span", {}, r.patient, el("br"), el("span", { class: "small muted" }, r.client)),
            ] as Col,
          ]
        : []),
      ["Service", (r) => r.service],
      [
        "Hours worked",
        (r) => el("span", {}, r.worked, el("br"), el("span", { class: "small muted" }, r.times)),
        true,
      ],
      ["Bill hours", (r) => r.billHours, true],
      ...(a.columns.bill ? [["Bill", (r: Row) => r.bill ?? "–", true] as Col] : []),
      ...(a.columns.pay ? [["Pay", (r: Row) => r.pay ?? "–", true] as Col] : []),
      ["Validation", (r) => chip(r.validation.text, r.validation.level)],
      ["Billing", (r) => chip(r.billing.text, r.billing.level)],
      ["Payroll", (r) => chip(r.payroll.text, r.payroll.level)],
      [
        "Documents",
        (r) => {
          if (!r.documents) return chip("Missing", "bad");
          const d = el(
            "button",
            {
              type: "button",
              class: "btn sm",
              "aria-label": `Documents for ${r.patient}, ${r.date}`,
            },
            icon("file"),
            ` ${r.documents}`,
          );
          d.addEventListener("click", () => void openVisitRecord(ctx, r.id, () => void load()));
          return d;
        },
      ],
      ...(a.can.edit
        ? [["", (r: Row) => ib("pencil", "Change this visit", () => void pencil(a, r))] as Col]
        : []),
    ];
    const lead = cols.findIndex((c) => c[0] === "Hours worked");
    const totalRow = (t: Totals, label: string) =>
      el(
        "tr",
        { class: "wvtot" },
        el("td"),
        el(
          "td",
          { colspan: String(lead) },
          el("b", {}, label),
          el("span", { class: "small muted" }, ` · ${t.visits} visit${t.visits === 1 ? "" : "s"}`),
        ),
        el("td", { class: "num" }, el("b", {}, t.worked)),
        el("td", { class: "num" }, el("b", {}, t.billHours)),
        ...(a.columns.bill ? [el("td", { class: "num" }, el("b", {}, t.bill ?? "–"))] : []),
        ...(a.columns.pay ? [el("td", { class: "num" }, el("b", {}, t.pay ?? "–"))] : []),
        el("td", {
          colspan: String(
            cols.length - lead - 2 - (a.columns.bill ? 1 : 0) - (a.columns.pay ? 1 : 0),
          ),
        }),
      );
    const rowEl = (r: Row) => {
      const box = el("input", {
        type: "checkbox",
        "aria-label": `Select ${r.patient}, ${r.date}`,
        checked: ticked.has(r.id),
      });
      box.addEventListener("change", () => {
        if (box.checked) ticked.add(r.id);
        else ticked.delete(r.id);
        drawActions(a);
        allBox.checked = a.rows.every((x) => ticked.has(x.id));
      });
      return el(
        "tr",
        { "data-visit": r.id },
        el("td", {}, box),
        ...cols.map(([, f, num]) => el("td", { class: num ? "num" : undefined }, f(r))),
      );
    };
    const allBox = el("input", {
      type: "checkbox",
      "aria-label": "Select all",
      checked: a.rows.length > 0 && a.rows.every((r) => ticked.has(r.id)),
    });
    allBox.addEventListener("change", () => {
      for (const r of a.rows)
        if (allBox.checked) ticked.add(r.id);
        else ticked.delete(r.id);
      draw(a);
    });
    const body: HTMLElement[] = [];
    if (a.group === "none") {
      body.push(...a.rows.map(rowEl), totalRow(a.totals, "Total"));
    } else {
      const keys = [...new Set(a.rows.map((r) => r.groupKey ?? ""))];
      for (const k of keys) {
        const g = a.groupTotals.find((x) => x.key === k);
        const label = g?.label ?? k;
        body.push(
          el(
            "tr",
            { class: "wvgrp" },
            el("td", { colspan: String(cols.length + 1) }, el("b", {}, label)),
          ),
          ...a.rows.filter((r) => (r.groupKey ?? "") === k).map(rowEl),
        );
        if (g) body.push(totalRow(g, `Total for ${label}`));
      }
    }
    return el(
      "div",
      { class: "tw" },
      el(
        "table",
        { class: "wvt" },
        el("caption", { class: "sr" }, a.title),
        el(
          "thead",
          {},
          el(
            "tr",
            {},
            el("th", { scope: "col" }, allBox),
            ...cols.map(([h, , num]) =>
              el(
                "th",
                { scope: "col", class: num ? "num" : undefined },
                h || el("span", { class: "sr" }, "Change"),
              ),
            ),
          ),
        ),
        el("tbody", {}, ...body),
      ),
    );
  }

  async function pencil(a: Answer, r: Row) {
    const [inText, outText] = r.times ? r.times.split(" – ") : ["", ""];
    const cin = field("Check in", inText === "–" ? "" : inText, { placeholder: "9:00 AM" });
    const cout = field("Check out", outText === "–" ? "" : outText, { placeholder: "1:00 PM" });
    const why = select(
      "Correction reason",
      [["", "Pick one when a time changes"], ...a.correctionReasons.map((x) => [x, x] as const)],
      "",
    );
    const bh = a.can.billHours ? field("Bill hours", r.billHours, { inputmode: "decimal" }) : null;
    const pay =
      a.can.pay && r.payValue !== null ? field("Pay", r.payValue, { inputmode: "decimal" }) : null;
    const fileId = `wvdoc-${r.id}`;
    const fileInput = el("input", { type: "file", id: fileId });
    const kind = select(
      "What it is",
      [["", "Pick what it is"], ...a.fileKinds.map((k) => [k.id, k.name] as const)],
      "",
    );
    const reason = field("Reason for the change", "", {
      placeholder: "e.g. Clocked out late, confirmed with the family",
      maxlength: "300",
    });
    const hist = el("p", { class: "small muted" });
    const showHistory = (lines: string[]) =>
      hist.replaceChildren(
        ...(lines.length ? [icon("history"), " "] : []),
        ...lines.flatMap((l, i) => (i ? [el("br"), l] : [l])),
      );
    const save = el("button", { type: "submit", class: "btn pri" }, icon("check"), " Save");
    const cancel = el("button", { type: "button", class: "btn" }, "Cancel");
    const form = el(
      "form",
      { class: "form", style: "max-width:none" },
      el(
        "div",
        { class: "grid2", style: "gap:10px" },
        cin.el,
        cout.el,
        ...(bh ? [bh.el] : []),
        ...(pay ? [pay.el] : []),
      ),
      why.el,
      el("div", { class: "field" }, el("label", { for: fileId }, "Document"), fileInput),
      kind.el,
      reason.el,
      hist,
      el("div", { class: "actions" }, save, cancel),
    );
    const m = openModal({
      title: `Change ${r.patient} · ${r.date}`,
      sub: `${r.caregiver} · ${r.service}`,
      body: [form],
    });
    cancel.addEventListener("click", () => m.close());
    void api<{ history: string[] }>("GET", `/api/worked-visits/${r.id}/history`).then(
      (h) => h.ok && showHistory(h.body.history),
    );
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (!reason.input.value.trim()) return refuse(m.note, "Say why it's changing.");
      save.disabled = true;
      const file = fileInput.files?.[0] ?? null;
      if (file) {
        if (!kind.input.value) {
          save.disabled = false;
          return refuse(m.note, "Pick what the document is.");
        }
        const up = await uploadFile(file, { kind: "visit", id: r.id, fileKind: kind.input.value });
        if (!up.ok) {
          save.disabled = false;
          return refuse(m.note, up.error);
        }
      }
      const res = await api<{ history: string[] }>("POST", `/api/worked-visits/${r.id}/change`, {
        in: cin.input.value,
        out: cout.input.value,
        correctionReason: why.input.value || null,
        ...(bh ? { billHours: bh.input.value } : {}),
        ...(pay ? { pay: pay.input.value } : {}),
        document: !!file,
        reason: reason.input.value,
      });
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      toast("Visit changed", "In the visit's history");
      await load();
      host.dispatchEvent(new Event("wv-changed"));
    });
    reason.input.focus();
  }

  exportBtn.addEventListener("click", async () => {
    exportBtn.disabled = true;
    const rows: Row[] = [];
    for (let page = 1; page <= 20; page++) {
      const res = await api<Answer>("GET", `/api/worked-visits?${params(page, 100)}`);
      if (!res.ok) break;
      rows.push(...res.body.rows);
      if (page >= res.body.pages) break;
    }
    exportBtn.disabled = false;
    const a = last;
    const head = [
      "Date",
      ...(a?.columns.caregiver ? ["Caregiver"] : []),
      ...(a?.columns.patient ? ["Patient", "Client"] : []),
      "Service",
      "Hours worked",
      "In and out",
      "Bill hours",
      ...(a?.columns.bill ? ["Bill"] : []),
      ...(a?.columns.pay ? ["Pay"] : []),
      "Validation",
      "Billing",
      "Payroll",
      "Documents",
    ];
    const cell = (v: string | number) => {
      const t = String(v);
      return /[",\n\r]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
    };
    const lines = [
      head,
      ...rows.map((r) => [
        r.date,
        ...(a?.columns.caregiver ? [r.caregiver] : []),
        ...(a?.columns.patient ? [r.patient, r.client] : []),
        r.service,
        r.worked,
        r.times,
        r.billHours,
        ...(a?.columns.bill ? [r.bill ?? ""] : []),
        ...(a?.columns.pay ? [r.pay ?? ""] : []),
        r.validation.text,
        r.billing.text,
        r.payroll.text,
        r.documents,
      ]),
    ];
    const blob = new Blob([`${lines.map((l) => l.map(cell).join(",")).join("\r\n")}\r\n`], {
      type: "text/csv",
    });
    const link = el("a", { href: URL.createObjectURL(blob), download: "Worked visits.csv" });
    document.body.append(link);
    link.click();
    link.remove();
    toast(`Exported ${rows.length} rows to CSV`);
  });

  void load();
  return host;
}
